import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Check, ChevronDown, ChevronsUpDown, Pencil, Plus, Search, X } from 'lucide-react';
import { cn } from '../../Lib/Cn';
import { Popover, PopoverTrigger, PopoverContent } from "../../Components/UI/popover";
import { Button } from "../../Components/UI/button";
import {
    Command,
    CommandList,
    CommandItem,
    CommandEmpty,
    CommandGroup,
    CommandSeparator,
    CommandInput
} from "../../Components/UI/command";
import { Label } from "../../Components/UI/label";
import MobileFullScreenDropdown from './MobileDropdown';
import useIsMobile from '../../Hooks/useIsMobile';
import { useSelector } from 'react-redux';
import { userIsAdmin } from '../../Store/Reducers';
import { getCurrentUser } from '../../Utils/Utilities';

// Updated CustomSearchableDropdown with mobile integration
const CustomSearchableDropdown = ({
    options = [],
    value,
    onSelect,
    placeholder = "Select option",
    displayKey = "label",
    valueKey = "value",
    iconKey = "icon", // Key for icon component in options
    allowAdd = false,
    addText = "Add new item",
    className = "",
    disabled = false,
    renderOption = null,
    emptyMessage = "No options found",
    fieldID,
    required,
    label,
    searchEnabled = true,
    searchPlaceholder = "Search...",
    noSort = false, // New prop to disable sorting
    // Optional override props
    forceMobile = false, // Force mobile UI regardless of screen size
    disableMobileUI = false, // Disable mobile UI completely
    enableMultiFieldSearch = false, // Enable multi-field search functionality
    enableVastSearch = false, // Enable vast search for large datasets
    showEditIcon = false, // Show edit icon for options
    onEditClick = null, // Callback when edit icon is clicked
    editPermissionCheck = null, // Function to check if user can edit specific option
    changeType = "",
    mobileDropdownText = "",
    cancellable = true,
    isIcon = false,
    showAddInHeader = true,
    openDialog = false,
    onClose = null,
    searchEnabledLength = 10,
    isMulti = false,
    hideSelectedChips = false,
    MultiSelect = false,
    popoverClassName = "",
    clearSearchOnSelect = true
}) => {
    const isMultiMode = isMulti || MultiSelect;
    const isMobile = useIsMobile(640);
    const shouldUseMobileUI = !disableMobileUI && (isMobile || forceMobile);
    const isAdmin = useSelector(userIsAdmin);
    const currentUser = getCurrentUser();
    const userClearedRef = useRef(false);

    // If we're on mobile (or forced), automatically use MobileFullScreenDropdown

    // Desktop/tablet UI (existing popover implementation)
    // Sort options alphabetically only if noSort is false
    const sortedOptions = noSort ? [...options] : [...options].sort((a, b) => {
        if ((typeof a === 'string' && a == 'Other') || a[displayKey] == 'Other') {
            return 1;
        }
        else if ((typeof b === 'string' && b == "Other") || b[displayKey] == 'Other') {
            return -1;
        }
        const aLabel = typeof a === 'string' ? a : a[displayKey];
        const bLabel = typeof b === 'string' ? b : b[displayKey];
        return aLabel?.localeCompare(bLabel);
    })

    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (openDialog) {
            setOpen(true);
        }
    }, [openDialog]);

    const [searchValue, setSearchValue] = useState("");
    const triggerRef = useRef(null);

    // Get selected option display value
    const getSelectedOptionLabel = () => {
        if (isMultiMode) {
            if (!value || !Array.isArray(value) || value.length === 0) return placeholder;
            return value.map(val => {
                const selectedOption = sortedOptions.find(option => {
                    const optionValue = typeof option === 'string' ? option : option[valueKey];
                    return optionValue === val;
                });
                return selectedOption ? (typeof selectedOption === 'string' ? selectedOption : selectedOption[displayKey]) : val;
            }).join(', ');
        }
        if (!value) {
            return placeholder;
        }
        const selectedOption = sortedOptions.find(option => {
            const optionValue = typeof option === 'string' ? option : option[valueKey];
            return optionValue === value;
        });
        return selectedOption ? (typeof selectedOption === 'string' ? selectedOption : selectedOption[displayKey]) : placeholder;
    };

    // Get selected option icon component
    const getSelectedOptionIcon = () => {
        if (isMultiMode || !value) return null;
        const selectedOption = sortedOptions.find(option => {
            const optionValue = typeof option === 'string' ? option : option[valueKey];
            return optionValue === value;
        });
        return selectedOption && typeof selectedOption === 'object' ? selectedOption[iconKey] : null;
    };

    const selectedLabel = getSelectedOptionLabel();
    const selectedIcon = getSelectedOptionIcon();
    const customConditionMobile = (addText.toLowerCase().includes("connection") || addText.toLowerCase().includes("contact") || addText.toLowerCase().includes("user")) ? allowAdd : allowAdd && (currentUser?.AgnConfig);
    const customCondition = (addText.toLowerCase().includes("connection") || addText.toLowerCase().includes("contact") || addText.toLowerCase().includes("user")) ? allowAdd : allowAdd && (isAdmin || currentUser?.AgnConfig);

    const handleSelect = (selectedValue, selectedOption = null) => {
        if (selectedValue === 'add-new-item') {
            onSelect('add', null, searchValue); // Pass searchValue as third parameter
            if (!isMultiMode) {
                setOpen(false);
                setSearchValue(""); // Clear search when closing
            }
        } else {
            const option = selectedOption || sortedOptions.find(opt => {
                const optionValue = typeof opt === 'string' ? opt : opt[valueKey];
                return optionValue === selectedValue;
            });
            if (isMultiMode) {
                const arr = Array.isArray(value) ? value : [];
                const newValue = arr.includes(selectedValue)
                    ? arr.filter(v => v !== selectedValue)
                    : [...arr, selectedValue];
                onSelect(newValue, option, searchValue);
                if (clearSearchOnSelect) {
                    setSearchValue("");
                }
            } else {
                onSelect(selectedValue, option, searchValue); // Pass searchValue as third parameter
                setOpen(false);
                setSearchValue(""); // Clear search when closing
            }
        }
    };

    // Handle keydown on trigger button to open dropdown and start typing
    const handleTriggerKeyDown = (e) => {
        if (disabled) return;

        if (e.key === 'Tab' || e.key === 'Shift' || e.key === 'Control' || e.key === 'Alt' || e.key === 'Meta') {
            return;
        }

        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(!open);
            return;
        }

        if (e.key === 'Escape') {
            setOpen(false);
            setSearchValue("");
            return;
        }

        if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
            e.preventDefault();

            if (!open) {
                // If dropdown is closed, start new search
                setSearchValue(e.key);
                setOpen(true);
            }
        }
    };
    useEffect(() => {
        if (open) {
            if (options.length === 0) {
                handleSelect('add-new-item');
            }

            if (searchValue.length > 0) {
                setTimeout(() => {
                    const input = document.querySelector('[cmdk-input]');
                    if (input) {
                        input.focus();
                        // Position cursor at the end
                        input.setSelectionRange(input.value.length, input.value.length);
                    }
                }, 10);
            }
        }
    }, [open, searchValue]);

    useEffect(() => {
        if (userClearedRef.current) return;
        if (sortedOptions.length === 1 && !value && !["TSK", "CITY", "PRD", "CAT", "CONN"].includes(changeType)) {
            const singleOption = sortedOptions[0];
            const optionValue = typeof singleOption === 'string' ? singleOption : singleOption[valueKey];
            handleSelect(optionValue, singleOption);
        }
    }, [sortedOptions.length, value]);

    if (shouldUseMobileUI) {
        return (
            <MobileFullScreenDropdown
                isMulti={isMultiMode}
                MultiSelect={MultiSelect}
                hideSelectedChips={hideSelectedChips}
                options={options}
                value={value}
                onSelect={onSelect}
                placeholder={placeholder}
                displayKey={displayKey}
                valueKey={valueKey}
                iconKey={iconKey}
                allowAdd={customConditionMobile}
                addText={addText}
                className={className}
                disabled={disabled}
                renderOption={renderOption}
                emptyMessage={emptyMessage}
                fieldID={fieldID}
                selectedLabel={selectedLabel}
                required={required}
                label={label}
                isIcon={isIcon}
                searchThreshold={10} // Default mobile search threshold
                searchPlaceholder={searchPlaceholder}
                noSort={noSort}
                enableMultiFieldSearch={enableMultiFieldSearch}
                enableVastSearch={enableVastSearch}
                showClearButton={cancellable}
                allchange={() => {
                    onSelect(null, null, null);
                }}
                showEditIcon={showEditIcon}
                onEditClick={onEditClick}
                editPermissionCheck={editPermissionCheck}
                isSorting={!noSort}
                mobileDropdownText={mobileDropdownText}
                userClearedRef={userClearedRef}
                showAddInHeader={showAddInHeader}
                openDialog={openDialog}
                onClose={onClose}
                clearSearchOnSelect={clearSearchOnSelect}
            />
        );
    }
    return (
        <div key={fieldID} className={className}>
            {/* <Label htmlFor={fieldID} required={required}>
                {label}
            </Label> */}

            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <div
                        ref={triggerRef}
                        id={fieldID}
                        data-field-id={fieldID}
                        role="combobox"
                        aria-expanded={open}
                        disabled={disabled}
                        onKeyDown={handleTriggerKeyDown}
                        onClick={() => {
                            if (!disabled) setOpen(!open);
                        }}
                        className={cn(
                            "w-full flex justify-between items-center font-normal min-h-11 text-sm text-left overflow-hidden py-0 px-0 sahi-input",
                            "border border-input rounded-md bg-white shadow-xs transition-[color,box-shadow] outline-none",
                            "hover:border-gray-400 disabled:cursor-not-allowed disabled:opacity-50",
                            disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                        )}
                    >
                        {isMultiMode ? (
                            <div className="flex flex-wrap gap-1.5 items-center w-full px-3 py-1 text-sm overflow-hidden flex-1 select-none">
                                {MultiSelect ? (
                                    (() => {
                                        const arr = Array.isArray(value) ? value : [];
                                        if (arr.length === 0) {
                                            return <span className="text-muted-foreground">{placeholder}</span>;
                                        }
                                        if (arr.length === 1) {
                                            const opt = sortedOptions.find(option => {
                                                const optionValue = typeof option === 'string' ? option : option[valueKey];
                                                return optionValue === arr[0];
                                            });
                                            const name = opt ? (typeof opt === 'string' ? opt : opt[displayKey]) : arr[0];
                                            return <span className="text-text-color font-medium truncate">{name}</span>;
                                        }

                                        // Avatar stack for 2+ selected attendees
                                        const getInitials = (nameStr) => {
                                            if (nameStr === null || nameStr === undefined) return '';
                                            const str = String(nameStr).trim();
                                            if (!str) return '';
                                            const parts = str.split(/\s+/);
                                            if (parts.length === 1) {
                                                return parts[0].substring(0, 2).toUpperCase();
                                            }
                                            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
                                        };

                                        const attendeesToShow = arr.slice(0, 4);
                                        const extraCount = arr.length - attendeesToShow.length;

                                        return (
                                            <div className="flex items-center gap-2 py-0.5">
                                                {/* Avatar Stack */}
                                                <div className="flex -space-x-2 overflow-hidden items-center">
                                                    {attendeesToShow.map(attendeeID => {
                                                        const opt = sortedOptions.find(option => {
                                                            const optionValue = typeof option === 'string' ? option : option[valueKey];
                                                            return optionValue === attendeeID;
                                                        });
                                                        const name = opt ? (typeof opt === 'string' ? opt : opt[displayKey]) : attendeeID;
                                                        const initials = getInitials(name);
                                                        return (
                                                            <div
                                                                key={attendeeID}
                                                                className="inline-flex items-center justify-center w-7 h-7 rounded-full border-2 border-white text-white text-[10px] font-semibold shrink-0 select-none shadow-xs"
                                                                style={{ backgroundColor: '#0b5d1e' }}
                                                                title={name}
                                                            >
                                                                {initials}
                                                            </div>
                                                        );
                                                    })}
                                                    {extraCount > 0 && (
                                                        <div className="inline-flex items-center justify-center w-7 h-7 rounded-full border-2 border-white bg-zinc-200 text-zinc-700 text-[10px] font-semibold shrink-0 select-none shadow-xs">
                                                            +{extraCount}
                                                        </div>
                                                    )}
                                                </div>
                                                {/* Count Label */}
                                                <span className="text-sm font-semibold text-text-color">
                                                    {arr.length} {label?.toLowerCase() || 'selected'}
                                                </span>
                                            </div>
                                        );
                                    })()
                                ) : hideSelectedChips ? (
                                    <span className="text-muted-foreground">{placeholder}</span>
                                ) : (
                                    (() => {
                                        const opts = [];
                                        const arr = Array.isArray(value) ? value : [];
                                        arr.forEach(val => {
                                            const opt = sortedOptions.find(option => {
                                                const optionValue = typeof option === 'string' ? option : option[valueKey];
                                                return optionValue === val;
                                            });
                                            if (opt) {
                                                opts.push({
                                                    value: val,
                                                    label: typeof opt === 'string' ? opt : opt[displayKey]
                                                });
                                            }
                                        });
                                        return opts.map(opt => (
                                            <span
                                                key={opt.value}
                                                className="inline-flex items-center gap-1 bg-zinc-100 text-zinc-800 text-xs font-semibold px-2 py-1 rounded border border-zinc-200"
                                            >
                                                {opt.label}
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const newValue = arr.filter(v => v !== opt.value);
                                                        onSelect(newValue);
                                                    }}
                                                    className="hover:bg-zinc-200 rounded-full p-0.5 text-zinc-500 hover:text-zinc-800 focus:outline-none touch-manipulation"
                                                >
                                                    <X className="h-3 w-3" />
                                                </button>
                                            </span>
                                        ));
                                    })()
                                )}
                                {!MultiSelect && !hideSelectedChips && (!value || value.length === 0) && (
                                    <span className="text-muted-foreground">{placeholder}</span>
                                )}
                            </div>
                        ) : (
                            <span
                                className={cn(
                                    (!value || selectedLabel === placeholder) ? "text-muted-foreground" : "text-gray-900",
                                    "text-sm px-3 truncate flex-1 flex-shrink min-w-0 flex items-center gap-2"
                                )}
                            >
                                <span className="truncate">{selectedLabel}</span>
                                {selectedIcon && <span className="flex-shrink-0">{selectedIcon}</span>}
                            </span>
                        )}
                        <div className="flex items-center gap-0 h-full">
                            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50 mr-2" />
                            {value > 0 && showEditIcon && onEditClick && editPermissionCheck && editPermissionCheck(sortedOptions.find(opt => {
                                const optionValue = typeof opt === 'string' ? opt : opt[valueKey];
                                return optionValue === value;
                            })) && (
                                    <div
                                        className={cn(
                                            "inline-flex items-center justify-center h-10 w-[35px] p-1",
                                            "border-l border-t-0 border-r-0 border-b-0 border-gray-300 cursor-pointer"
                                        )}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const option = sortedOptions.find(opt => {
                                                const optionValue = typeof opt === 'string' ? opt : opt[valueKey];
                                                return optionValue === value;
                                            });
                                            onEditClick(option);
                                        }}
                                    >
                                        <div className="w-10 h-7 flex items-center justify-center">
                                            <Pencil className="h-3 w-3 text-gray-600" />
                                        </div>
                                    </div>
                                )}
                            {(value > 0 && cancellable && !MultiSelect) && (
                                <div
                                    className={cn(
                                        "inline-flex items-center justify-center h-10 w-[35px] p-1",
                                        "border-l border-t-0 border-r-0 border-b-0 border-gray-300 cursor-pointer"
                                    )}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        userClearedRef.current = true;
                                        onSelect(null, null, null);
                                    }}
                                >
                                    <div className="w-10 h-7 flex items-center justify-center">
                                        <X
                                            className="h-4 w-4 text-gray-600"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </PopoverTrigger>

                <PopoverContent
                    className={cn("p-0 bg-white border shadow-lg z-[10050]", popoverClassName)}
                    style={{ width: "var(--radix-popover-trigger-width)" }}
                >
                    {options.length == 0 ? null : <Command
                        filter={(value, search) => {
                            if (!search) return 1;
                            const s = search.toLowerCase();
                            const v = value?.toLowerCase() || '';
                            if (!enableMultiFieldSearch) {
                                if (v.startsWith(s)) return 2;
                                return v.includes(s) ? 1 : 0;
                            }

                            // Custom multi-field search for connections
                            const parts = value ? value.split('___') : [];
                            const optIndex = parts.length > 1 ? parseInt(parts[parts.length - 1], 10) : NaN;
                            const option = !isNaN(optIndex) && sortedOptions[optIndex]
                                ? sortedOptions[optIndex]
                                : sortedOptions.find(opt => {
                                    const optionLabel = typeof opt === 'string' ? opt : opt[displayKey];
                                    return optionLabel?.toLowerCase() === value?.toLowerCase();
                                });

                            if (!option || !option.searchableText) {
                                return value?.toLowerCase().includes(search?.toLowerCase()) ? 1 : 0;
                            }

                            if (!enableVastSearch) {
                                return option?.searchableText.includes(search?.toLowerCase()) ? 1 : 0;
                            } else {

                                // Split search query into individual words
                                const searchWords = search?.toLowerCase().trim().split(/\s+/);
                                const searchableTextLower = option.searchableText.toLowerCase();
                                const segmentLower = (typeof option === 'string' ? option : option[displayKey])?.toLowerCase();

                                // For each search word, check if it matches in searchableText
                                for (const searchWord of searchWords) {
                                    if (!searchableTextLower.includes(searchWord)) {
                                        return 0; // Word not found anywhere, exclude this option
                                    }

                                    // Check if this search word starts any word in the segment
                                    const segmentWords = segmentLower.split(/[\s&]+/).filter(w => w.length > 0);
                                    const startsAnyWord = segmentWords.some(segmentWord =>
                                        segmentWord.startsWith(searchWord)
                                    );

                                    if (!startsAnyWord) {
                                        // Check if it's in the middle of a segment word
                                        const inMiddleOfSegmentWord = segmentWords.some(segmentWord =>
                                            segmentWord.includes(searchWord) && !segmentWord.startsWith(searchWord)
                                        );

                                        if (inMiddleOfSegmentWord) {
                                            return 0; // Exclude results where search word is in middle of segment word
                                        }
                                    }
                                }

                                return 1; // All words match and none are in middle of segment words
                            }

                        }}
                    >
                        {(searchEnabled && sortedOptions.length > searchEnabledLength) && (
                            <CommandInput
                                placeholder={searchPlaceholder}
                                value={searchValue}
                                onValueChange={setSearchValue}
                                className="h-9"
                                autoFocus={open}
                                onKeyDown={(e) => {
                                    // Prevent the Command component from handling certain keys
                                    if (e.key === 'Escape') {
                                        e.stopPropagation();
                                        setOpen(false);
                                        setSearchValue("");
                                        triggerRef.current?.focus();
                                    }
                                }}
                            />
                        )}
                        <CommandList
                            className="max-h-[200px] overflow-y-auto"
                            onWheel={(e) => e.stopPropagation()}
                        >
                            <CommandEmpty className="!py-0">
                                <div className="px-3 py-2">
                                    <div className="text-sm text-muted-foreground text-left">
                                        {emptyMessage || `No ${label?.toLowerCase()} found.`}
                                    </div>
                                </div>
                            </CommandEmpty>

                            <CommandGroup className="!py-0">
                                {sortedOptions.map((option, index) => {
                                    const optionValue = typeof option === 'string' ? option : (option[valueKey] !== undefined ? option[valueKey] : (option.id ?? option.ID ?? option.value));
                                    const optionLabel = typeof option === 'string' ? option : (option[displayKey] ?? option.name ?? option.label);
                                    const optionIcon = typeof option === 'object' ? option[iconKey] : null;

                                    const itemSearchValue = typeof option === 'string'
                                        ? `${option}___${index}`
                                        : `${optionLabel || ''} ${option?.agencyName || ''} ${option?.fullAgencyName || ''} ${option?.searchableText || ''} ${optionValue ?? ''}___${index}`;

                                    const isSelected = isMulti
                                        ? (Array.isArray(value) && value.includes(optionValue))
                                        : value === optionValue;

                                    return (
                                        <CommandItem
                                            key={`${optionValue}-${index}`}
                                            value={itemSearchValue}
                                            onSelect={() => handleSelect(optionValue, option)}
                                            className={cn(
                                                "cursor-pointer",
                                                isSelected && "font-semibold"
                                            )}
                                        >
                                            <div className="flex items-center w-full">
                                                <div className="min-w-0 flex items-center w-full">
                                                    {renderOption ? (
                                                        renderOption(option, isSelected)
                                                    ) : (
                                                        <div className="flex items-center justify-between w-full">
                                                            <div className="flex items-center min-w-0">
                                                                {isMulti && (
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isSelected}
                                                                        readOnly
                                                                        className="mr-2.5 h-4 w-4 rounded border-gray-300 text-sahi-primary-color accent-sahi-primary-color shrink-0 cursor-pointer"
                                                                    />
                                                                )}
                                                                <span className="truncate">
                                                                    {optionLabel}
                                                                    {typeof option === 'object' && option?.agencyName && (
                                                                        <span className="opacity-50 text-xs ml-1.5 font-normal">
                                                                            ({option.agencyName})
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            </div>
                                                            {optionIcon && <span className="flex-shrink-0">{optionIcon}</span>}
                                                        </div>
                                                    )}
                                                </div>
                                                {!isMulti && isSelected && <div className="flex items-center gap-1 ml-2">
                                                    <Check
                                                        className="h-4 w-4 opacity-100"
                                                    />
                                                </div>}
                                            </div>
                                        </CommandItem>
                                    );
                                })}
                            </CommandGroup>

                            {/* Add New Option Button - Always show at bottom when allowAdd is true */}
                            {(customCondition) && (
                                <div className="p-2 border-t">
                                    <Button
                                        onClick={() => handleSelect('add-new-item')}
                                        size="sm"
                                        className="bg-sahi-green-light text-foreground hover:border-sahi-green-light hover:text-foreground !text-xs"
                                    >
                                        <Plus className="h-4 w-4" />
                                        {addText}
                                    </Button>
                                </div>
                            )}
                        </CommandList>
                    </Command>}
                </PopoverContent>
            </Popover>
        </div>
    );
};

export default CustomSearchableDropdown;